<script lang="ts">
	// Services
	import { format } from '../services/i18n';
	import { createEventDispatcher } from 'svelte';
	// Design system components
	import { Input, Text, ProgressBar, Icon, Popover } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar, strengthValidation } from '../helpers/utilities';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import type { InputPropsTypes, ProgressBarPropsTypes } from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	const dispatcher = createEventDispatcher();

	export let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'];
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		class: string;
		autocomplete: string;
	};
	export let error;

	let blur = false;
	let windowClientWidth: number;
	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: score = strengthValidation(inputProps.value);
	$: [strengthText, strengthBarType] = evaluateStrength(score);

	$: if (inputProps.value && !isMobile) {
		inputProps.class = 'width-full';
	} else {
		inputProps.class = 'mb-m width-full';
	}

	$: requirements = [
		{
			text: $format('text_password_requirement_lowercase', {
				default: 'One lowercase',
			}),
			check: /[a-z]/.test(inputProps.value),
		},
		{
			text: $format('text_password_requirement_uppercase', {
				default: 'One uppercase',
			}),
			check: /[A-Z]/.test(inputProps.value),
		},
		{
			text: $format('text_password_requirement_number', {
				default: 'One digit',
			}),
			check: /\d/.test(inputProps.value),
		},
	].map((item) => ({
		...item,
		icon: item.check ? 'fa-solid fa-check' : 'fa-solid fa-circle-small',
		iconColor: item.check ? 'text-success' : 'text-color-muted',
		textColor: item.check ? '' : 'text-color-muted',
	}));

	// get password strength description and type for the strength bar,
	// based on password score
	const evaluateStrength = (score: number): [string, ProgressBarPropsTypes['type']] => {
		let _description: string = 'weak';
		let _barType: ProgressBarPropsTypes['type'] = 'danger';

		if (score < 60) {
			_description = $format('password_strength_description_weak', {
				default: 'Weak',
			});
			_barType = 'danger';
		} else if (score >= 60 && score < 90) {
			_description = $format('password_strength_description_average', {
				default: 'Average',
			});
			_barType = 'warning';
		} else {
			_description = $format('password_strength_description_excellent', {
				default: 'Excellent',
			});
			_barType = 'success';
		}

		return [_description, _barType];
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#if isMobile}
	<Popover
		class="d-flex width-full"
		show
		hideSlotCustomContent={blur || !inputProps.value}
		placement="top"
		hideCloseBtn
		fixedWidth
	>
		<Input
			class={inputProps.class}
			icon={inputProps.icon}
			size={inputProps.size}
			bind:value={inputProps.value}
			label={inputProps.label === 'password'
				? capFirstChar($format('placeholder_password'))
				: capFirstChar($format('placeholder_new_password'))}
			aria-labelledby="input-plfhs5e"
			type={inputProps.type}
			disabled={inputProps.disabled}
			{error}
			on:click={() => dispatcher('click')}
			on:enter={() => dispatcher('enter')}
			on:focus={() => {
				blur = false;
				dispatcher('focus');
			}}
			on:blur={() => (blur = true)}
			on:input={(e) => dispatcher('input', e)}
			id="input-plfhs5e"
		/>
		<svelte:fragment slot="customContent">
			{#each requirements as { icon, text, iconColor, textColor }}
				<div class="text-left">
					<Icon {icon} size="xs" class={iconColor} />
					<Text class="mt-s {textColor}" styleType="body-small" style="display:inline-block;">
						{text}
					</Text>
				</div>
			{/each}
			<ProgressBar class="width-full mt-s" type={strengthBarType} progress={score} />
			<div class="text-left">
				<Text class="text-bold mt-s" styleType="body-small" style="display:inline-block;"
					>{$format('text_password_strength', {
						default: 'Password strength:',
					})}</Text
				>
				<Text class="mt-s" styleType="body-small" style="display:inline-block;"
					>{strengthText}</Text
				>
			</div>
		</svelte:fragment>
	</Popover>
{:else}
	<Input
		class={inputProps.class}
		icon={inputProps.icon}
		size={inputProps.size}
		bind:value={inputProps.value}
		label={inputProps.label === 'password'
			? capFirstChar($format('placeholder_password'))
			: capFirstChar($format('placeholder_new_password'))}
		type={inputProps.type}
		disabled={inputProps.disabled}
		autocomplete={inputProps.autocomplete}
		{error}
		on:click={() => dispatcher('click')}
		on:enter={() => dispatcher('enter')}
		on:focus={() => {
			blur = false;
			dispatcher('focus');
		}}
		on:blur={() => (blur = true)}
		on:input={(e) => dispatcher('input', e)}
		id="input-iq4q94r"
		aria-labelledby="input-iq4q94r"
	/>
	{#if inputProps.value}
		<ProgressBar class="width-full mt-s" type={strengthBarType} progress={score} />
		<div class="text-left">
			<Text class="text-bold mt-s" styleType="body-small" style="display:inline-block;"
				>{$format('text_password_strength', {
					default: 'Password strength:',
				})}</Text
			>
			<Text class="mt-s" styleType="body-small" style="display:inline-block;">{strengthText}</Text>
		</div>
		{#each requirements as { icon, text, iconColor, textColor }, index}
			<div
				class={classMap({
					'text-left': true,
					'mb-m': index === requirements.length - 1,
				})}
			>
				<Icon {icon} size="xs" class={iconColor} />
				<Text class="mt-s {textColor}" styleType="body-small" style="display:inline-block;">
					{text}
				</Text>
			</div>
		{/each}
	{/if}
{/if}
